/**
 * IntegrationsDashboard.tsx
 *
 * Integrations dashboard for importing content from social media
 * and managing API keys.
 */

import React, { useState, useEffect } from "react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  Key,
  Copy,
  Eye,
  EyeOff,
  Settings,
  DollarSign,
  Trash2,
  Video,
  Loader2,
  X,
  Youtube,
  Instagram,
  CheckCircle
} from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/contexts/AuthContext";
import { useToast } from "@/components/ui/use-toast";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import DashboardLayout from "@/components/layout/DashboardLayout";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle
} from "@/components/ui/alert-dialog";

interface APIKey {
  id: string;
  name: string;
  key: string;
  created_at: string;
  last_used_at?: string;
  usage_count: number;
  is_active: boolean;
}

interface CreditBalance {
  balance: number;
  spent: number;
}

interface Posit {
  id: string;
  title: string;
}


type ContentPlatform = 'tiktok' | 'youtube' | 'instagram';

interface UnifiedContentImportState {
  platform: ContentPlatform;
  // Common fields
  profileUrl: string;
  dateFilter: string;
  selectedPosits: string[];
  isScraping: boolean;
  progress: {
    status: 'idle' | 'scraping' | 'processing' | 'complete' | 'error';
    message: string;
    itemsProcessed: number;
    totalItems: number;
  };
}

const IntegrationsDashboard: React.FC = () => {
  const [apiKeys, setApiKeys] = useState<APIKey[]>([]);
  const [creditBalance, setCreditBalance] = useState<CreditBalance>({
    balance: 0,
    spent: 0
  });
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState("import");
  const [showKey, setShowKey] = useState<{ [key: string]: boolean }>({});
  const [newKeyName, setNewKeyName] = useState("");
  const [creatingKey, setCreatingKey] = useState(false);
  const [deletingKeyId, setDeletingKeyId] = useState<string | null>(null);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [keyToDelete, setKeyToDelete] = useState<{ id: string; name: string } | null>(null);

  // Unified Content Import State
  const [contentImportState, setContentImportState] = useState<UnifiedContentImportState>({
    platform: 'tiktok',
    profileUrl: '',
    dateFilter: '30days',
    selectedPosits: [],
    isScraping: false,
          progress: {
            status: 'idle',
            message: '',
            itemsProcessed: 0,
            totalItems: 0
          }
  });
  const [availablePosits, setAvailablePosits] = useState<Posit[]>([]);
  
  const { user } = useAuth();
  const { toast } = useToast();

  useEffect(() => {
    if (user?.id) {
      console.log('User authenticated:', user.id);
      fetchDashboardData();
      fetchUserPosits();
    } else {
      console.log('No user authenticated');
    }

    // Check if user returned from Stripe payment
    const urlParams = new URLSearchParams(window.location.search);
    const paymentStatus = urlParams.get('payment');
    const amount = urlParams.get('amount');

    if (paymentStatus === 'success' && amount && user) {
      handlePaymentSuccess(parseFloat(amount));
    }

    if (paymentStatus === 'cancelled') {
      toast({
        title: "Payment Cancelled",
        description: "Your payment was cancelled. No charges were made.",
        variant: "destructive",
      });
      // Clean up URL
      window.history.replaceState({}, document.title, window.location.pathname);
    }
  }, [user]);

  // Cleanup on unmount - reset any ongoing imports
  useEffect(() => {
    return () => {
      // Reset state when component unmounts (user navigates away)
      if (contentImportState.isScraping) {
        console.log('Cleaning up ongoing import due to navigation...');
        setContentImportState(prev => ({
          ...prev,
          isScraping: false,
          progress: { status: 'idle', message: '', itemsProcessed: 0, totalItems: 0 }
        }));
      }
    };
  }, [contentImportState.isScraping]);

  const handlePaymentSuccess = async (amount: number) => {
    try {
      
      // Get session
      const { data: session } = await supabase.auth.getSession();
      if (!session.session) {
        throw new Error('No session');
      }
      
      // Update user credits directly in database
      const response = await supabase.functions.invoke('manage-api-keys', {
        headers: {
          Authorization: `Bearer ${session.session.access_token}`,
          'Content-Type': 'application/json',
        },
        body: {
          action: 'add_credits',
          amount: amount
        }
      });
      
      if (response.error) {
        throw new Error(response.error.message);
      }
      
      // Show success message
      toast({
        title: "Payment Successful! 🎉",
        description: `$${amount} in API credits added to your account`,
      });
      
      // Clean up URL
      window.history.replaceState({}, document.title, window.location.pathname);
      
      // Refresh the dashboard data
      setTimeout(() => {
        fetchDashboardData();
      }, 1000);
      
    } catch (error) {
      toast({
        title: "Payment Processed",
        description: "Your payment was successful, but there was an issue updating your credits. Please contact support.",
        variant: "destructive",
      });
    }
  };

  const fetchDashboardData = async () => {
    try {
      setLoading(true);
      
      // Fetch real API keys and usage stats
      const { data: session } = await supabase.auth.getSession();
      if (!session?.session?.access_token) {
        throw new Error('No valid session');
      }

      // Fetch API keys using Supabase functions invoke
      const keysResponse = await supabase.functions.invoke('manage-api-keys?action=list', {
        headers: {
          Authorization: `Bearer ${session.session.access_token}`,
        },
      });

      if (keysResponse.error) {
        // Handle specific subscription requirement error
        if (keysResponse.error.message?.includes('requires Posit Plus')) {
          setApiKeys([]);
          return; // Don't throw error, just show empty state
        }
        throw new Error(keysResponse.error.message);
      }

      const keysData = keysResponse.data;
      const apiKeysData = keysData?.api_keys?.map((key: any) => ({
        id: key.id,
        name: key.name,
        key: key.key, // Use the formatted key that backend already provides (e.g., "pk_bb316...")
        created_at: key.created_at,
        last_used_at: key.last_used_at,
        usage_count: key.usage_count || 0,
        is_active: key.is_active
      })) || [];

      // Fetch credit balance
      const { data: userData, error: userError } = user?.id ? await supabase
        .from('users')
        .select('api_credits, api_credits_spent')
        .eq('id', user.id)
        .single() : { data: null, error: null };

      if (userData && !userError) {
        setCreditBalance({
          balance: (userData as any).api_credits || 0,
          spent: (userData as any).api_credits_spent || 0
        });
      } else {
      }

      setApiKeys(apiKeysData);
    } catch (error) {
      toast({
        title: "Error",
        description: "Failed to load dashboard data. Please try again.",
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  const fetchUserPosits = async () => {
    if (!user?.id) return;

    try {
      console.log('Fetching posits for user:', user.id);

      const { data: posits, error } = await supabase
        .from('posits')
        .select('id, title')
        .eq('user_id', user.id)
        .order('created_at', { ascending: false });

      if (error) {
        console.error('Error fetching posits:', error);
        toast({
          title: "Error loading posits",
          description: "Could not fetch your available posits.",
          variant: "destructive"
        });
        return;
      }

      console.log('Fetched posits:', posits);
      setAvailablePosits(posits || []);
    } catch (error) {
      console.error('Error fetching user posits:', error);
      toast({
        title: "Error loading posits",
        description: "Could not fetch your available posits.",
        variant: "destructive"
      });
    }
  };

  const purchaseCredits = async (amount: number) => {
    try {
      const { data: session } = await supabase.auth.getSession();
      if (!session?.session?.access_token) {
        throw new Error('Please log in to purchase credits');
      }

      const user = session.session.user;
      if (!user?.email || !user?.id) {
        throw new Error('User information not found');
      }


      // Direct Stripe payment links (NO Edge Functions!)
      const paymentLinks = {
        10: 'https://buy.stripe.com/00w9AUbgaccwblr4Nm5EY05',
        25: 'https://buy.stripe.com/6oU00kfwq1xS9dj6Vu5EY06', 
        50: 'https://buy.stripe.com/6oU8wQdoi2BW89f6Vu5EY07',
        100: 'https://buy.stripe.com/dRm4gAac66Sccpvgw45EY08'
      };

      const baseUrl = paymentLinks[amount as keyof typeof paymentLinks];
      
      if (!baseUrl) {
        throw new Error(`Credit amount $${amount} not supported. Choose $10, $25, $50, or $100.`);
      }

      // Add user identification parameters
      const url = new URL(baseUrl);
      url.searchParams.set('prefilled_email', user.email || '');
      url.searchParams.set('client_reference_id', user.id || '');

      
      // Direct redirect - NO Edge Functions needed!
      window.location.href = url.toString();

    } catch (error) {
      toast({
        title: "Error",
        description: error instanceof Error ? error.message : "Failed to purchase credits",
        variant: "destructive",
      });
    }
  };


  const startTikTokScrape = async () => {
    console.log('startTikTokScrape called with state:', contentImportState);

    if (!contentImportState.profileUrl.trim()) {
      console.log('No profile URL provided');
      toast({
        title: "Profile Required",
        description: "Please enter a TikTok profile to scrape.",
        variant: "destructive"
      });
      return;
    }

    if (contentImportState.selectedPosits.length === 0) {
      console.log('No posits selected');
      toast({
        title: "Posits Required",
        description: "Please select at least one Posit to import the content to.",
        variant: "destructive"
      });
      return;
    }

    console.log('TikTok scrape function called - state already set by button click');

    try {
      // Prepare the scrape configuration
      console.log('Preparing TikTok scrape config...');
      const config = {
        profiles: [contentImportState.profileUrl.startsWith('@') ? contentImportState.profileUrl : `@${contentImportState.profileUrl}`],
        hashtags: [],
        videoUrls: [],
        dateFilter: contentImportState.dateFilter,
        maxItems: 50, // Default limit
        selectedPosits: contentImportState.selectedPosits,
        positId: contentImportState.selectedPosits[0], // Use first selected posit
        userId: user?.id || ''
      };
      console.log('TikTok config prepared:', config);

      const { data, error } = await supabase.functions.invoke('scrape-tiktok-v1', {
        body: { config }
      });

      if (error) {
        console.error('Supabase function error:', error);
        throw error;
      }

      console.log('TikTok scrape response:', data);
      setContentImportState(prev => ({
        ...prev,
        progress: {
          status: data.success ? 'complete' : 'error',
          message: data.success ? 'TikTok content imported successfully!' : 'Failed to import content',
          itemsProcessed: data.itemsProcessed || 0,
          totalItems: data.itemsProcessed || 0
        },
        isScraping: false
      }));

      if (data.success) {
        toast({
          title: "TikTok Import Complete! 🎉",
          description: `Successfully imported ${data.itemsProcessed} items to your Posit.`,
        });
      } else {
        throw new Error(data.errors?.[0] || 'Scraping failed');
      }

    } catch (error) {
      console.error('TikTok scraping error:', error);
      setContentImportState(prev => ({
        ...prev,
        progress: {
          status: 'error',
          message: error instanceof Error ? error.message : 'Unknown error occurred',
          itemsProcessed: 0,
          totalItems: 0
        },
        isScraping: false
      }));

      toast({
        title: "TikTok Import Failed",
        description: error instanceof Error ? error.message : "An error occurred during import.",
        variant: "destructive"
      });
    }
  };

  const startYoutubeScrape = async () => {
    console.log('startYoutubeScrape called with state:', contentImportState);

    if (!contentImportState.profileUrl.trim()) {
      console.log('No channel URL provided');
      toast({
        title: "Channel Required",
        description: "Please enter a YouTube channel to scrape.",
        variant: "destructive"
      });
      return;
    }

    if (contentImportState.selectedPosits.length === 0) {
      console.log('No posits selected');
      toast({
        title: "Posits Required",
        description: "Please select at least one Posit to import the content to.",
        variant: "destructive"
      });
      return;
    }

    console.log('YouTube scrape function called - state already set by button click');

    try {
      // Prepare the scrape configuration
      console.log('Preparing YouTube scrape config...');
      const config = {
        channelUrl: contentImportState.profileUrl,
        dateFilter: contentImportState.dateFilter,
        maxItems: 50, // Default limit
        selectedPosits: contentImportState.selectedPosits,
        positId: contentImportState.selectedPosits[0], // Use first selected posit
        userId: user?.id || ''
      };
      console.log('YouTube config prepared:', config);

      const { data, error } = await supabase.functions.invoke('scrape-youtube-v1', {
        body: { config }
      });

      if (error) {
        console.error('Supabase function error:', error);
        throw error;
      }

      console.log('YouTube scrape response:', data);
      setContentImportState(prev => ({
        ...prev,
        progress: {
          status: data.success ? 'complete' : 'error',
          message: data.success ? 'YouTube content imported successfully!' : 'Failed to import content',
          itemsProcessed: data.itemsProcessed || 0,
          totalItems: data.itemsProcessed || 0
        },
        isScraping: false
      }));

      if (data.success) {
        toast({
          title: "YouTube Import Complete! 🎉",
          description: `Successfully imported ${data.itemsProcessed} videos to your Posit.`,
        });
      } else {
        throw new Error(data.errors?.[0] || 'Scraping failed');
      }

    } catch (error) {
      console.error('YouTube scraping error:', error);
      setContentImportState(prev => ({
        ...prev,
        progress: {
          status: 'error',
          message: error instanceof Error ? error.message : 'Unknown error occurred',
          itemsProcessed: 0,
          totalItems: 0
        },
        isScraping: false
      }));

      toast({
        title: "YouTube Import Failed",
        description: error instanceof Error ? error.message : "An error occurred during import.",
        variant: "destructive"
      });
    } finally {
      setContentImportState(prev => ({ ...prev, isScraping: false }));
    }
  };

  const generateApiKey = async () => {
    if (!newKeyName.trim()) {
      toast({
        title: "Error",
        description: "Please enter a name for your API key.",
        variant: "destructive",
      });
      return;
    }

    try {
      setCreatingKey(true);
      
      const { data: session } = await supabase.auth.getSession();
      if (!session?.session?.access_token) {
        throw new Error('No valid session');
      }

      const requestBody = { name: newKeyName.trim() };

      const response = await supabase.functions.invoke('manage-api-keys?action=create', {
        headers: {
          Authorization: `Bearer ${session.session.access_token}`,
          'Content-Type': 'application/json',
        },
        body: requestBody,
      });


      if (response.error) {
        throw new Error(response.error.message);
      }

      const responseData = response.data;
      
      // Check for function-level errors (400 status codes etc.)
      if (responseData?.error) {
        throw new Error(responseData.message || responseData.error);
      }

      const createdKey = responseData.api_key;
      
      const newKey: APIKey = {
        id: createdKey.id,
        name: createdKey.name,
        key: createdKey.key, // Full key returned only once
        created_at: createdKey.created_at,
        usage_count: 0,
        is_active: createdKey.is_active
      };

      setApiKeys(prev => [newKey, ...prev]);
      setNewKeyName("");
      
      toast({
        title: "API Key Created",
        description: responseData.message || "API key created successfully. Make sure to copy it now - you won't be able to see it again.",
      });
      
      // Refresh the dashboard data to ensure consistency
      setTimeout(() => {
        fetchDashboardData();
      }, 1000);
    } catch (error) {
      toast({
        title: "Error Creating API Key",
        description: error instanceof Error ? error.message : "Failed to create API key. Please try again.",
        variant: "destructive",
      });
    } finally {
      setCreatingKey(false);
    }
  };

  const copyToClipboard = (text: string, label: string) => {
    navigator.clipboard.writeText(text);
    toast({
      title: "Saved to Clipboard!",
      description: `${label} has been copied to your clipboard. You can now paste it into your application.`,
    });
  };

  const toggleKeyVisibility = (keyId: string) => {
    setShowKey(prev => ({ ...prev, [keyId]: !prev[keyId] }));
  };

  const openDeleteDialog = (keyId: string, keyName: string) => {
    setKeyToDelete({ id: keyId, name: keyName });
    setDeleteDialogOpen(true);
  };

  const handleConfirmDelete = async () => {
    if (!keyToDelete) return;
    
    await deleteApiKey(keyToDelete.id, keyToDelete.name);
    setDeleteDialogOpen(false);
    setKeyToDelete(null);
  };

  const deleteApiKey = async (keyId: string, keyName: string) => {
    
    try {
      setDeletingKeyId(keyId);
      
      const { data: session } = await supabase.auth.getSession();
      if (!session?.session?.access_token) {
        throw new Error('No valid session');
      }

      const requestBody = { key_id: keyId, action: 'delete' };

      const response = await supabase.functions.invoke('manage-api-keys', {
        headers: {
          Authorization: `Bearer ${session.session.access_token}`,
          'Content-Type': 'application/json',
        },
        body: requestBody,
      });

      
      if (response.error) {
        
        // Try to extract the actual error message from the server
        let errorMessage = 'Unknown error occurred';
        
        if (response.error.message) {
          errorMessage = response.error.message;
        }
        
        // Add status code information if available
        if (response.error.status) {
          if (response.error.status === 404) {
            errorMessage = 'API key not found or you do not have permission to delete it';
          } else if (response.error.status === 401) {
            errorMessage = 'Authentication required. Please log in again.';
          } else if (response.error.status === 403) {
            errorMessage = 'Permission denied. This API key may belong to another user.';
          }
        }
        
        throw new Error(errorMessage);
      }

      const responseData = response.data;
      
      // Check for function-level errors
      if (responseData?.error) {
        throw new Error(responseData.message || responseData.error);
      }

      // Additional validation for successful deletion

      // If we reach here, deletion was successful (no errors thrown)
      
      // Remove the key from local state
      setApiKeys(prev => {
        const newKeys = prev.filter(key => key.id !== keyId);
        return newKeys;
      });
      
      toast({
        title: "API Key Deleted Successfully",
        description: `The API key "${keyName}" has been permanently deleted and can no longer be used.`,
      });
      
    } catch (error) {
      toast({
        title: "Error Deleting API Key",
        description: error instanceof Error ? error.message : "Failed to delete API key. Please try again.",
        variant: "destructive",
      });
    } finally {
      setDeletingKeyId(null);
    }
  };

  const maskApiKey = (key: string) => {
    // Backend already returns masked format like "pk_bb316..."
    // If it's already masked (ends with "..."), return as-is
    if (key.endsWith('...')) {
      return key;
    }
    // If it's a full key (only shown once after creation), mask it properly
    return `${key.substring(0, 12)}${'•'.repeat(20)}${key.substring(key.length - 4)}`;
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  if (loading) {
    return (
      <DashboardLayout
        title="Integrations"
        description="Import content from social media and manage your API keys"
      >
        <div className="animate-pulse space-y-6">
          <div className="grid gap-3 grid-cols-1 md:grid-cols-4">
            {Array(4).fill(0).map((_, i) => (
              <div key={i} className="h-24 bg-muted rounded"></div>
            ))}
          </div>
          <div className="h-96 bg-muted rounded"></div>
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout
      title="Integrations"
      description="Import content from social media and manage your API keys"
    >
      {/* Main Content Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
        <TabsList className="grid w-full grid-cols-2 h-auto gap-1">
          <TabsTrigger value="import" className="flex items-center gap-2 text-xs md:text-sm p-2 md:p-3">
            <Video className="h-3 w-3 md:h-4 md:w-4" />
            Import Content
          </TabsTrigger>

          <TabsTrigger value="overview" className="flex items-center gap-2 text-xs md:text-sm p-2 md:p-3">
            <Settings className="h-3 w-3 md:h-4 md:w-4" />
            API Keys
          </TabsTrigger>
        </TabsList>

        <TabsContent value="overview" className="mt-6">
          <div className="space-y-6">
            {/* Credit Purchase Section */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <DollarSign className="h-5 w-5" />
                  Purchase API Credits
                </CardTitle>
                <CardDescription>
                  Load credits to your account for API usage. $0.10 per API request.
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div className="bg-card border rounded-lg p-4 text-center">
                    <div className="text-2xl font-bold">${creditBalance.balance.toFixed(2)}</div>
                    <p className="text-sm text-muted-foreground">Current Balance</p>
                  </div>
                  <div className="bg-card border rounded-lg p-4 text-center">
                    <div className="text-2xl font-bold">${creditBalance.spent.toFixed(2)}</div>
                    <p className="text-sm text-muted-foreground">Total Spent</p>
                  </div>
                  <div className="bg-card border rounded-lg p-4 text-center">
                    <div className="text-2xl font-bold">{Math.floor(creditBalance.balance / 0.10)}</div>
                    <p className="text-sm text-muted-foreground">API Calls Available</p>
                  </div>
                </div>
                
                <div className="flex gap-4 justify-center">
                  <Button 
                    onClick={() => purchaseCredits(10)}
                    className="flex-1 max-w-32"
                  >
                    $10
                  </Button>
                  <Button 
                    onClick={() => purchaseCredits(25)}
                    className="flex-1 max-w-32"
                  >
                    $25
                  </Button>
                  <Button 
                    onClick={() => purchaseCredits(50)}
                    className="flex-1 max-w-32"
                  >
                    $50
                  </Button>
                  <Button 
                    onClick={() => purchaseCredits(100)}
                    className="flex-1 max-w-32"
                  >
                    $100
                  </Button>
                </div>
                
                {creditBalance.balance < 1.00 && (
                  <div className="bg-muted border rounded-lg p-4">
                    <div className="flex items-center gap-2 mb-2">
                      <DollarSign className="h-5 w-5" />
                      <span className="font-medium">Minimum Balance Required</span>
                    </div>
                    <p className="text-sm text-muted-foreground">
                      You need at least $1.00 in credits to use the API. Please purchase credits above.
                    </p>
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Create New API Key */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Key className="h-5 w-5" />
                  Create New API Key
                </CardTitle>
                <CardDescription>
                  Generate a new API key to access Posit's endpoints from external tools
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="bg-muted border rounded-lg p-4">
                  <div className="flex items-center gap-2 mb-2">
                    <DollarSign className="h-5 w-5" />
                    <span className="font-medium">Premium API Access</span>
                  </div>
                  <p className="text-sm text-muted-foreground">
                    API access requires a Posit Plus subscription and <strong>$1.00 minimum credit balance</strong>. 
                    Each API call deducts $0.10 from your prepaid credits.
                  </p>
                </div>
                {/* Credit Balance Warning */}
                {creditBalance.balance < 1.0 && (
                  <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4 mb-4">
                    <div className="flex items-center space-x-2">
                      <DollarSign className="h-4 w-4 text-yellow-600" />
                      <span className="text-sm font-medium text-yellow-800">
                        Minimum $1.00 credit balance required
                      </span>
                    </div>
                    <p className="text-sm text-yellow-700 mt-1">
                      Current balance: ${creditBalance.balance.toFixed(2)}. 
                      Purchase credits above to create API keys.
                    </p>
                  </div>
                )}

                <div className="flex gap-4">
                  <div className="flex-1">
                    <Label htmlFor="keyName">Key Name</Label>
                    <Input
                      id="keyName"
                      placeholder="e.g., n8n Production, Development Testing"
                      value={newKeyName}
                      onChange={(e) => setNewKeyName(e.target.value)}
                    />
                  </div>
                  <div className="flex items-end">
                    <Button 
                      onClick={generateApiKey}
                      disabled={creatingKey || !newKeyName.trim() || creditBalance.balance < 1.0}
                    >
                      {creatingKey ? "Creating..." : 
                       creditBalance.balance < 1.0 ? "Insufficient Credits" : 
                       "Generate Key"}
                    </Button>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Existing API Keys */}
            <Card>
              <CardHeader>
                <CardTitle>Your API Keys</CardTitle>
                <CardDescription>
                  Manage your existing API keys and monitor their usage
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                {apiKeys.length === 0 ? (
                  <div className="text-center py-8 text-muted-foreground">
                    <Key className="h-12 w-12 mx-auto mb-4 opacity-50" />
                    <p>No API keys yet. Create your first one above to get started.</p>
                  </div>
                ) : (
                  apiKeys.map((apiKey) => (
                    <div key={apiKey.id} className="border rounded-lg p-4 space-y-3">
                      <div className="flex items-center justify-between">
                        <div>
                          <h4 className="font-medium">{apiKey.name}</h4>
                          <p className="text-sm text-muted-foreground">
                            Created {formatDate(apiKey.created_at)}
                          </p>
                        </div>
                        <div className="flex items-center gap-2">
                          <Badge variant={apiKey.is_active ? "default" : "secondary"}>
                            {apiKey.is_active ? "Active" : "Inactive"}
                          </Badge>
                        </div>
                      </div>
                      
                      <div className="flex items-center gap-2">
                        <div className="flex-1 font-mono text-sm bg-muted p-2 rounded">
                          {showKey[apiKey.id] ? apiKey.key : maskApiKey(apiKey.key)}
                        </div>
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => toggleKeyVisibility(apiKey.id)}
                        >
                          {showKey[apiKey.id] ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                        </Button>
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => copyToClipboard(apiKey.key, "API key")}
                        >
                          <Copy className="h-4 w-4" />
                        </Button>
                        <Button
                          size="sm"
                          variant="destructive"
                          onClick={() => openDeleteDialog(apiKey.id, apiKey.name)}
                          disabled={deletingKeyId === apiKey.id}
                        >
                          {deletingKeyId === apiKey.id ? (
                            <div className="animate-spin h-4 w-4 border-2 border-white border-t-transparent rounded-full" />
                          ) : (
                            <Trash2 className="h-4 w-4" />
                          )}
                        </Button>
                      </div>
                      
                      <div className="flex items-center justify-between text-sm text-muted-foreground">
                        <span>{apiKey.usage_count.toLocaleString()} total calls</span>
                        {apiKey.last_used_at && (
                          <span>Last used {formatDate(apiKey.last_used_at)}</span>
                        )}
                      </div>
                    </div>
                  ))
                )}
              </CardContent>
            </Card>
          </div>
        </TabsContent>

        <TabsContent value="import" className="mt-6">
          <div className="space-y-6">
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Video className="h-5 w-5" />
                  Content Import
                </CardTitle>
                <CardDescription>
                  Import content from TikTok, YouTube, and Instagram directly into your Posits
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-6">
                {/* Platform Selector */}
                <div className="space-y-2">
                  <Label>Select Platform</Label>
                  <Select
                    value={contentImportState.platform}
                    onValueChange={(value: ContentPlatform) => {
                      setContentImportState(prev => ({
                        ...prev,
                        platform: value,
                        profileUrl: '', // Reset URL when switching platforms
                        dateFilter: '30days', // Reset to default
                        selectedPosits: [],
                        progress: { status: 'idle', message: '', itemsProcessed: 0, totalItems: 0 },
                        isScraping: false
                      }));
                    }}
                  >
                    <SelectTrigger className="w-full">
                      <SelectValue placeholder="Choose a platform" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="tiktok">
                        <div className="flex items-center gap-2">
                          <Video className="h-4 w-4" />
                          TikTok
                        </div>
                      </SelectItem>
                      <SelectItem value="youtube">
                        <div className="flex items-center gap-2">
                          <Youtube className="h-4 w-4" />
                          YouTube
                        </div>
                      </SelectItem>
                      <SelectItem value="instagram">
                        <div className="flex items-center gap-2">
                          <Instagram className="h-4 w-4" />
                          Instagram (Coming Soon)
                        </div>
                      </SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                {/* Progress Display */}
                {contentImportState.progress.status !== 'idle' && (
                  <Card className="border-muted">
                    <CardContent className="pt-6">
                      <div className="flex items-center justify-between mb-4">
                        <div className="flex items-center gap-3">
                          {contentImportState.progress.status === 'scraping' && <Loader2 className="h-5 w-5 animate-spin text-primary" />}
                          {contentImportState.progress.status === 'processing' && <Loader2 className="h-5 w-5 animate-spin text-primary" />}
                          {contentImportState.progress.status === 'complete' && <CheckCircle className="h-5 w-5 text-green-600" />}
                          {contentImportState.progress.status === 'error' && <X className="h-5 w-5 text-red-500" />}
                          <div>
                            <h3 className="font-semibold text-lg">Import Progress</h3>
                            <p className="text-sm text-muted-foreground">{contentImportState.progress.message}</p>
                          </div>
                        </div>
                        <Badge variant={
                          contentImportState.progress.status === 'complete' ? 'default' :
                          contentImportState.progress.status === 'error' ? 'destructive' : 'secondary'
                        }>
                          {contentImportState.progress.status === 'scraping' && 'Discovering'}
                          {contentImportState.progress.status === 'processing' && 'Processing'}
                          {contentImportState.progress.status === 'complete' && 'Complete'}
                          {contentImportState.progress.status === 'error' && 'Error'}
                        </Badge>
                      </div>

                      {/* Progress Bar */}
                      {contentImportState.progress.totalItems > 0 && (
                        <div className="space-y-3 mb-4">
                          <div className="flex justify-between text-sm">
                            <span>Progress</span>
                            <span className="text-primary font-medium">
                              {contentImportState.progress.itemsProcessed}/{contentImportState.progress.totalItems}
                            </span>
                          </div>
                          <div className="w-full bg-muted rounded-full h-2">
                            <div
                              className="bg-primary h-2 rounded-full transition-all duration-300"
                              style={{ width: `${(contentImportState.progress.itemsProcessed / contentImportState.progress.totalItems) * 100}%` }}
                            />
                          </div>
                        </div>
                      )}


                      {/* Completion Message */}
                      {contentImportState.progress.status === 'complete' && contentImportState.progress.itemsProcessed > 0 && (
                        <div className="mt-4 text-center">
                          <div className="inline-flex items-center gap-2 text-green-700 bg-green-50 px-4 py-2 rounded-lg border border-green-200">
                            <CheckCircle className="h-5 w-5" />
                            <span className="font-medium">
                              Successfully imported {contentImportState.progress.itemsProcessed} items
                            </span>
                          </div>
                        </div>
                      )}
                    </CardContent>
                  </Card>
                )}

                {/* Dynamic Content Based on Platform */}
                <div className="space-y-4">
                  <div className="space-y-2">
                    <Label htmlFor="content-url">
                      {contentImportState.platform === 'tiktok' ? 'TikTok Profile' :
                       contentImportState.platform === 'youtube' ? 'YouTube Channel' :
                       'Instagram Profile'}
                    </Label>
                    <Input
                      id="content-url"
                      placeholder={
                        contentImportState.platform === 'tiktok' ? '@username or profile URL' :
                        contentImportState.platform === 'youtube' ? '@username, channel URL, or playlist URL' :
                        'Instagram username or profile URL'
                      }
                      value={contentImportState.profileUrl}
                      onChange={(e) => setContentImportState(prev => ({ ...prev, profileUrl: e.target.value }))}
                      disabled={contentImportState.isScraping || contentImportState.platform === 'instagram'}
                      className="w-full"
                    />
                    <p className="text-xs text-muted-foreground">
                      {contentImportState.platform === 'tiktok' && 'Enter a TikTok username (@username) or full profile URL'}
                      {contentImportState.platform === 'youtube' && 'Enter a YouTube channel (@username), channel URL, or playlist URL'}
                      {contentImportState.platform === 'instagram' && 'Instagram integration coming soon!'}
                    </p>
                  </div>

                  {/* Date Filter */}
                  <div className="space-y-2">
                    <Label htmlFor="date-filter">Import Content From</Label>
                    <Select
                      value={contentImportState.dateFilter || '30days'}
                      onValueChange={(value) => setContentImportState(prev => ({ ...prev, dateFilter: value }))}
                      disabled={contentImportState.isScraping || contentImportState.platform === 'instagram'}
                    >
                      <SelectTrigger className="w-full">
                        <SelectValue placeholder="Select time period" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="7days">Last 7 days</SelectItem>
                        <SelectItem value="30days">Last 30 days</SelectItem>
                        <SelectItem value="6months">Last 6 months</SelectItem>
                        <SelectItem value="1year">Last year</SelectItem>
                        <SelectItem value="5years">Last 5 years</SelectItem>
                      </SelectContent>
                    </Select>
                    <p className="text-xs text-muted-foreground">
                      Import content posted within the selected time period
                    </p>
                  </div>

                  {/* Posit Selection */}
                  <div className="space-y-2">
                    <Label>Select Posits</Label>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-2 max-h-32 overflow-y-auto border rounded-md p-2">
                      {availablePosits.map((posit) => (
                        <div key={posit.id} className="flex items-center space-x-2">
                          <Checkbox
                            id={`content-posit-${posit.id}`}
                            checked={contentImportState.selectedPosits.includes(posit.id)}
                            onCheckedChange={(checked) => {
                              setContentImportState(prev => ({
                                ...prev,
                                selectedPosits: checked
                                  ? [...prev.selectedPosits, posit.id]
                                  : prev.selectedPosits.filter(id => id !== posit.id)
                              }));
                            }}
                            disabled={contentImportState.isScraping}
                          />
                          <Label
                            htmlFor={`content-posit-${posit.id}`}
                            className="text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70"
                          >
                            {posit.title}
                          </Label>
                        </div>
                      ))}
                    </div>
                    {availablePosits.length === 0 && (
                      <p className="text-sm text-muted-foreground">
                        No Posits available. Create a Posit first.
                      </p>
                    )}
                  </div>


                </div>

                {/* Import Button */}
                <Button
                  onClick={async () => {
                    console.log('🔥 Import button clicked!');
                    console.log('Platform:', contentImportState.platform);
                    console.log('Profile URL:', contentImportState.profileUrl);
                    console.log('Selected Posits:', contentImportState.selectedPosits);
                    console.log('Date Filter:', contentImportState.dateFilter);

                                  // Immediately set loading state for better UX
                    setContentImportState(prev => ({
                      ...prev,
                      isScraping: true,
                      progress: {
                        status: 'scraping',
                        message: `Finding content from ${contentImportState.platform}...`,
                        itemsProcessed: 0,
                        totalItems: 0
                      },
                    }));

                            if (contentImportState.platform === 'tiktok') {
                              console.log('🚀 Starting TikTok scrape...');
                              await startTikTokScrape();
                            } else if (contentImportState.platform === 'youtube') {
                              console.log('🚀 Starting YouTube scrape...');
                              await startYoutubeScrape();
                            } else {
                      console.log('📸 Instagram selected - showing coming soon message');
                      // Reset loading state for Instagram
                      setContentImportState(prev => ({
                        ...prev,
                        isScraping: false,
                        progress: { status: 'idle', message: '', itemsProcessed: 0, totalItems: 0 }
                      }));
                      toast({
                        title: "Coming Soon",
                        description: "Instagram integration is coming soon!",
                      });
                    }
                  }}
                  disabled={
                    !contentImportState.profileUrl ||
                    contentImportState.selectedPosits.length === 0 ||
                    contentImportState.isScraping ||
                    availablePosits.length === 0 ||
                    contentImportState.platform === 'instagram'
                  }
                  className="w-full"
                  size="lg"
                >
                  {contentImportState.isScraping ? (
                    <>
                      <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                      Importing {contentImportState.platform === 'tiktok' ? 'TikTok' :
                                contentImportState.platform === 'youtube' ? 'YouTube' :
                                'Instagram'} Content...
                    </>
                  ) : (
                    <>
                      {contentImportState.platform === 'tiktok' ? <Video className="h-4 w-4 mr-2" /> :
                       contentImportState.platform === 'youtube' ? <Youtube className="h-4 w-4 mr-2" /> :
                       <Instagram className="h-4 w-4 mr-2" />}
                      Import {contentImportState.platform === 'tiktok' ? 'TikTok' :
                             contentImportState.platform === 'youtube' ? 'YouTube' :
                             'Instagram'} Content
                    </>
                  )}
                </Button>
              </CardContent>
            </Card>
          </div>
        </TabsContent>

      </Tabs>

      {/* Delete Confirmation Dialog */}
      <AlertDialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete API Key</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to delete the API key "{keyToDelete?.name}"? This action cannot be undone and will immediately revoke access for any applications using this key.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleConfirmDelete}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
              disabled={deletingKeyId === keyToDelete?.id}
            >
              {deletingKeyId === keyToDelete?.id ? "Deleting..." : "Delete Key"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </DashboardLayout>
  );
};

export default IntegrationsDashboard;
